import { ErrorState } from "@/components/ErrorState";
import Loader from "@/components/loader/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from "@/components/ui/sheet";

import {
  useAddAsset,
  useMyWalletAndAssets,
  useToggleAsset,
  useTokens,
} from "@/hooks";
import { type WalletAsset } from "@/interfaces/asset";
import { useAppSelector } from "@/store/hooks";
import { removeTrailingToken } from "@/utils";
import { ArrowLeft, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { Token } from "@/interfaces/token";
import { Skeleton } from "@/components/ui/skeleton";
import AssetItem from "@/components/shared/asset/AssetItem";

const UserAssets = () => {
  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState<"name" | "date" | "balance">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  const {
    data: myWallet,
    isLoading: isLoading,
    isError: isError,
    refetch,
  } = useMyWalletAndAssets(userId!);

  console.log("MY WALLET WITH ASSETS:", myWallet);

  const addAssetMutation = useAddAsset(userId);

  const toggleAssetMutation = useToggleAsset(userId);

  const assets: WalletAsset[] = myWallet?.assets || [];

  // Filtering + sorting
  const filteredAssets: WalletAsset[] = useMemo(() => {
    let result = assets.filter((asset) => {
      const matchesSearch =
        asset?.token?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset?.currency?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset?.contractAddress
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase());

      const matchesFilter =
        filter === "all" ||
        (filter === "active" && asset.isActive) ||
        (filter === "inactive" && !asset.isActive) ||
        (filter === "zero" && asset.available === "0") ||
        (filter === "nonzero" && asset.available !== "0");

      return matchesSearch && matchesFilter;
    });

    result
      .sort((a, b) => {
        let comparison = 0;

        switch (sortBy) {
          case "name":
            comparison = a.token?.name.localeCompare(b.token?.name);
            break;
          case "date":
            comparison =
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            break;
          case "balance":
            comparison = Number(a.balance) - Number(b.balance);
            break;
        }

        return sortOrder === "asc" ? comparison : -comparison;
      })
      .sort((a, b) => {
        const balanceDiff =
          Number(b.convertedBalance ?? 0) - Number(a.convertedBalance ?? 0);

        if (balanceDiff !== 0) return balanceDiff;

        return (a.token?.name ?? "").localeCompare(b.token?.name ?? "");
      });

    return result;
  }, [assets, searchQuery, filter, sortBy, sortOrder]);

  const {
    data: tokenData,
    isLoading: isLoadingTokens,
    isError: isErrorTokens,
    refetch: refetchTokens,
  } = useTokens(1, 30, debouncedSearch);

  console.log("TOKENS", tokenData);

  const activeAssets: WalletAsset[] = myWallet?.assets || [];
  const tokenAssets: Token[] = tokenData?.data || [];

  console.log("ACTIVE", activeAssets);

  const activeTokenIds = new Set(activeAssets.map((a) => a.tokenId));

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const filteredInactive = useMemo(() => {
    return tokenAssets.filter((token) => !activeTokenIds.has(token.id));
  }, [tokenAssets, activeAssets]);

  const handleAssetAction = (id: string, shouldEnable: boolean) => {
    if (shouldEnable) {
      addAssetMutation.mutate({
        tokenId: id,
      });
      return;
    }

    toggleAssetMutation.mutate(id);
  };

  if (isLoading) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Button
        className="hidden sm:flex"
        variant="ghost"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>
      <div className="flex items-center justify-between">
        <span>All My Assets</span>
        <Sheet>
          <SheetTrigger asChild>
            <Button className="h-10 rounded-full font-medium cursor-pointer">
              <Plus className="w-4 h-4" />
              Add more
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[90vh]">
            <SheetHeader>
              <div className="relative w-full max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search new assets/currencies to add..."
                  className={cn(
                    "w-full pl-10 pr-4 py-2.5 text-sm",
                    "rounded-full border border-border",
                    "bg-card shadow-sm",
                    "focus:outline-none focus:ring-2 focus:ring-primary/30",
                  )}
                />
              </div>
            </SheetHeader>
            <div className="rounded-xl border bg-card overflow-hidden">
              {filteredInactive.map((token, idx) => (
                <AssetItem
                  key={token.id}
                  asset={token}
                  variant="add"
                  isLast={idx === filteredInactive.length - 1}
                  isPending={addAssetMutation.isPending}
                  onAdd={() => handleAssetAction(token.id, true)}
                />
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by token name, currency, contract..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm"
          />
        </div>

        <div className="flex items-center gap-3">
          {/* <Filter className="w-4 h-4 text-muted-foreground" /> */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 bg-card border border-border rounded-lg text-sm"
          >
            <option value="all">All Assets</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="zero">Zero Balance</option>
            <option value="nonzero">Non-Zero Balance</option>
          </select>

          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split("-");
              setSortBy(field as any);
              setSortOrder(order as any);
            }}
            className="px-4 py-2 bg-card border border-border rounded-lg text-sm"
          >
            <option value="date-desc">Newest</option>
            <option value="date-asc">Oldest</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="balance-desc">Highest Balance</option>
            <option value="balance-asc">Lowest Balance</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        {filteredAssets.map((asset) => (
          <AssetItem key={asset.id} asset={asset} />
        ))}
      </div>
    </div>
  );
};

export default UserAssets;
